/**
 * Regional Managers and Territory Managers (owner decisions 2026-10-29, "RM and TM Role Set up.pdf").
 *
 * They are real roles (regional_manager, territory_manager) that start from a manager's access, minus:
 *   - modules they do not use (REQ-1.1 + owner: only what's specified): Campaigns, Content, Form Handlers,
 *     Interactions, Landing Pages, Segments, Sequences, Finance, Support Tasks, Tasks, Vouchers / Offers, SMS;
 *   - event configuration (REQ-2.1): they create events, then view and run them, never change their setup;
 *   - five event tabs (REQ-2.2) and four event types (REQ-2.3).
 * The database enforces the same rules (migrations 2026-10-29-1240/1241; is_field_manager()). Everything here is the
 * matching UI, so they are never offered a control the server would refuse.
 */
import { normalizeRole } from "./roleUtils";

export const FIELD_MANAGER_ROLES = [
  "regional_manager",
  "territory_manager",
] as const;

export function isFieldManagerRole(role: string | null | undefined): boolean {
  const r = normalizeRole(role);
  return r === "regionalmanager" || r === "territorymanager";
}

/** REQ-2.2: event tabs they do not get. */
export const FIELD_MANAGER_HIDDEN_EVENT_TABS: ReadonlySet<string> = new Set([
  "drawing",
  "campaigns",
  "tasks",
  "lists",
  "finance", // "Budget & Expenses"
]);

/**
 * Event tabs that are event SETUP: shown so they can see how the event is set up, but read-only (the database refuses
 * the writes). Overview, Operations, Attendees, Comments, Confirm Schedule, Verification and Reports stay live: that is
 * running the event and managing their reps.
 */
export const FIELD_MANAGER_READ_ONLY_EVENT_TABS: ReadonlySet<string> = new Set([
  "tickets",
  "cta",
  "landing-page",
  "rewards",
  "vouchers",
  "sharing",
]);

/** REQ-2.3: event types they cannot create (the database refuses the insert too). */
export const FIELD_MANAGER_HIDDEN_EVENT_TYPES: ReadonlySet<string> = new Set([
  "conference",
  "networking_event",
  "member_check_in",
  "lightning_deals",
]);

/**
 * Sidebar for RM / TM: the allowed modules only (a curated list, like the MSR portal, so a new module never appears
 * for them by default). Paths must match router.tsx routes that allow the two roles.
 */
export const FIELD_MANAGER_NAV_PATHS: ReadonlySet<string> = new Set([
  "/dashboard",
  "/contacts",
  "/events",
  "/organizations",
  "/partners",
  "/redemptions",
  "/reports",
  "/sales-activities/phone-sales",
  "/sales-calendar",
  "/msr-team",
  "/sales-team",
  "/commission-estimator",
  "/settings",
  "/whats-new",
]);

/**
 * Routes whose RequireRole lists "manager" but deliberately NOT the two field-manager roles (each one is a module or
 * action the owner excluded). roleRouting.test.ts fails if a manager route is in neither state, so a new manager
 * route cannot silently leave RMs/TMs out (or let them in).
 */
export const FIELD_MANAGER_EXCLUDED_ROUTES: ReadonlySet<string> = new Set([
  "/partner-dashboard", // partner portal view
  "/screen-pop", // Five9 support screen pop
  "/email-subscription-groups", // email marketing
  "/admin/ticket-rewards", // admin
  "/segments",
  "/suppression-list", // email marketing
  "/tasks",
  "/events/import", // bulk event setup (they create events one at a time)
  "/events/:id/edit", // event configuration
  "/interactions",
  "/calendar-requests",
  "/campaigns",
  "/campaigns/create",
  "/campaigns/lookups",
  "/campaigns/lookups/new",
  "/campaigns/lookups/:id",
  "/campaigns/:campaignId/flights/:flightId",
  "/campaigns/:id",
  "/offers",
  "/sequences",
  "/sequences/create",
  "/sequences/triage",
  "/sequences/:id",
  "/sequences/:id/edit",
  "/account-reviews",
  "/account-reviews/create",
  "/account-reviews/:id",
  "/content",
  "/landing-pages",
  "/landing-pages/create",
  "/landing-pages/:id",
  "/partner-offers",
  "/partner-offers/:id",
]);

/** RM / TM position scope (migrations 1270 / 1276): the Lead/Event Rep must be on their team; the manager levels may be
 *  their team or the chain above them. The database refuses anything else. */
export interface FieldManagerPositionScope {
  team: ReadonlySet<string>;
  above: ReadonlySet<string>;
}

export function fieldManagerAllows(
  scope: FieldManagerPositionScope,
  slot: "repId" | "tmId" | "rmId" | "sdId",
  id: string | null,
): boolean {
  if (!id) {
    return true;
  }
  return scope.team.has(id) || (slot !== "repId" && scope.above.has(id));
}
